import React, { useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";

import { ohQueryClient } from "@shared/libs/react-query/query-client";
import { CollaboratorQueryKeys } from "@shared/libs/react-query/query-keys.constants";
import { eventLegalEntitySwitch } from "amplitude/legal-entity";
import { browserHistory } from "hassibot/browser-history";
import { get, set } from "hassibot/local-storage";
import {
  hasUnsavedWork,
  UNSAVED_WORK_MESSAGE,
} from "hassibot/services_v2/service-worker/unsaved-work-registry";
import { applyLegalModeNamespace } from "hassibot/translations";
import { useSessionCollaboratorContext } from "startup/inside-components";
import { Loading } from "startup/outside-components";

import { findClosestRedirectionListView } from "./current-legal-entity-provider.utils";
import {
  CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY,
  CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY,
  FORCE_SWITCH_LEGAL_ENTITY_PARAM,
} from "./current-legal-entity.constants";
import { invalidateLegalEntitySessionCache } from "./legal-entity-session-cache";
import { indexedDBCache } from "./services_v2/common/cache";
import { LegalEntity, LegalMode } from "./services_v2/common/types";

interface CurrentLegalEntityContextType {
  currentLegalEntity: LegalEntity;
  setCurrentLegalEntity: (newLegalEntity: LegalEntity) => void;
}

const CurrentLegalEntityContext = React.createContext<CurrentLegalEntityContextType | undefined>(
  undefined
);

export const CurrentLegalEntityProvider = ({ children }) => {
  const { sessionCollaborator } = useSessionCollaboratorContext();

  const params = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);
  const paramsLegalEntityUuid = params.get(FORCE_SWITCH_LEGAL_ENTITY_PARAM);

  const { myAccessibleLegalEntities } = sessionCollaborator;

  const getDefaultLegalEntity = (): LegalEntity => {
    if (myAccessibleLegalEntities.length === 0) {
      throw new Error("No legal entity found for the current user");
    }

    const paramsLegalEntity = myAccessibleLegalEntities.find(
      le => le.uuid === paramsLegalEntityUuid
    );
    if (paramsLegalEntity) {
      return paramsLegalEntity;
    }

    // Get initial value from local storage or arbitrarily use the first value from the given list
    const legalEntityUuidFromLocalStorage = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const legalModeFromLocalStorage = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);
    const currentLegalEntity =
      myAccessibleLegalEntities.find(
        legalEntity =>
          legalEntityUuidFromLocalStorage === legalEntity.uuid &&
          legalModeFromLocalStorage === legalEntity.mode
      ) ?? myAccessibleLegalEntities[0];

    return currentLegalEntity;
  };

  const [legalEntity, setLegalEntity] = useState<LegalEntity>(getDefaultLegalEntity);

  // Set the i18n default namespace (contractor vs mandatary) during render,
  // BEFORE the children — and the lazy app chunks whose module-scope `t()` maps
  // bake against it — mount. This is a deliberate render-phase side effect, not
  // a derived value: do NOT move it to a useEffect, which runs after mount, too
  // late for those chunks. `applyLegalModeNamespace` is idempotent.
  applyLegalModeNamespace(legalEntity.mode);

  // Also render-phase: the static-context query reads this IndexedDB from a
  // descendant's render, before any effect runs. Idempotent.
  indexedDBCache.initLegalEntityDatabase(legalEntity);

  // On a fresh device the localStorage keys are empty: we seed them below
  // WITHOUT reloading. Children (incl. the lazy app shell, which reads the
  // legal mode via `getCurrentLegalMode()` and bakes module-scope `t()`
  // values) are gated until then so they never load under an unseeded entity.
  // On a warm load the stored values already match → ready immediately, no
  // extra loading screen.
  const [isReady, setIsReady] = useState<boolean>(() => {
    const storedUuid = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const storedMode = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);
    return storedUuid === legalEntity.uuid && storedMode === legalEntity.mode;
  });

  const [isSwitching, setIsSwitching] = useState<boolean>(false);

  const setCurrentLegalEntity = useCallback(
    (newLegalEntity: LegalEntity): void => {
      if (newLegalEntity.uuid === legalEntity.uuid) {
        return;
      }

      if (hasUnsavedWork() && !window.confirm(UNSAVED_WORK_MESSAGE)) {
        return;
      }

      eventLegalEntitySwitch(
        { uuid: legalEntity.uuid, mode: legalEntity.mode },
        { uuid: newLegalEntity.uuid, mode: newLegalEntity.mode }
      );

      set(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY, newLegalEntity.uuid);
      set(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY, newLegalEntity.mode);
      invalidateLegalEntitySessionCache();

      indexedDBCache.initLegalEntityDatabase(newLegalEntity);

      flushSync(() => setIsSwitching(true));

      ohQueryClient.removeQueries({
        predicate: query => query.queryKey[0] !== CollaboratorQueryKeys.ME,
      });
      ohQueryClient.resetQueries({ queryKey: [CollaboratorQueryKeys.ME] });

      // Leave a detail view for its closest list view (its URL points to a
      // previous-entity resource); if already on the list view, strip a stale
      // query string / hash with a replace. Unmatched path: stay, remount refetches.
      const closestListView = findClosestRedirectionListView(window.location.pathname);
      if (closestListView !== undefined) {
        if (closestListView !== window.location.pathname) {
          browserHistory.push(closestListView);
        } else if (window.location.search !== "" || window.location.hash !== "") {
          browserHistory.replace(closestListView);
        }
      }

      setLegalEntity(newLegalEntity);
      setIsSwitching(false);
    },
    [legalEntity.uuid, legalEntity.mode]
  );

  useLayoutEffect(() => {
    indexedDBCache.initLegalEntityDatabase(legalEntity);
  }, [legalEntity]);

  useLayoutEffect(() => {
    const legalEntityUuidFromLocalStorage = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const legalModeFromLocalStorage = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);
    if (
      legalEntityUuidFromLocalStorage === legalEntity.uuid &&
      legalModeFromLocalStorage === legalEntity.mode
    ) {
      return;
    }

    set(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY, legalEntity.uuid);
    set(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY, legalEntity.mode);
    invalidateLegalEntitySessionCache();

    // Force-switch through the URL parameter: clean the parameter with a full
    // navigation, which also reboots the app under the just-stored entity.
    if (paramsLegalEntityUuid) {
      params.delete(FORCE_SWITCH_LEGAL_ENTITY_PARAM);
      const newSearch = params.toString() ? `?${params.toString()}` : "";
      const redirect = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.location.href = redirect;
      return;
    }

    // Boot-time mismatch only (a deliberate switch returns early — it writes
    // localStorage before setState): seed the keys. If they pointed to a now-
    // inaccessible entity, redirect to its list view and reset the cache (/me refetch).
    const isFirstSeed =
      legalEntityUuidFromLocalStorage === null || legalModeFromLocalStorage === null;
    if (!isFirstSeed) {
      const closestListView = findClosestRedirectionListView(window.location.pathname);
      if (closestListView !== undefined) {
        browserHistory.replace(closestListView);
      }
      ohQueryClient.resetQueries();
    }
    setIsReady(true);
  }, [legalEntity, params, paramsLegalEntityUuid]);

  if (!isReady || isSwitching) {
    return <Loading />;
  }

  // key remounts the whole subtree on entity change. React flushes the
  // initLegalEntityDatabase layout effect before any passive effect (react-query
  // fetches from an observer subscription), so the per-entity DB is re-targeted first.
  return (
    <CurrentLegalEntityContext.Provider
      value={{ currentLegalEntity: legalEntity, setCurrentLegalEntity }}
    >
      <React.Fragment key={legalEntity.uuid}>{children}</React.Fragment>
    </CurrentLegalEntityContext.Provider>
  );
};

export const useLegalMode = () => {
  const context = useContext(CurrentLegalEntityContext);

  if (!context) {
    throw new Error(
      "The application context is missing, add 'CurrentLegalEntityProvider' at the top of component hierarchy"
    );
  }

  const { currentLegalEntity, setCurrentLegalEntity } = context;

  const isMandatary = currentLegalEntity.mode === LegalMode.MANDATARY;
  const isContractor = currentLegalEntity.mode === LegalMode.CONTRACTOR;
  const isSandbox = currentLegalEntity.isSandbox;

  return {
    mode: currentLegalEntity.mode,
    isMandatary,
    isContractor,
    isSandbox,
    currentLegalEntity,
    setCurrentLegalEntity,
  };
};
